import to from 'await-to-js';
import { Node } from 'contensis-delivery-api';
import { ContentType, Component } from 'contensis-core-api';
import deepCleaner from 'deep-cleaner';
import pl from 'pluralize';

import NodesMigrationService from './NodesMigrationService';
import {
  MigrateModelsResult,
  Model,
  ResourcesToMigrate,
  MigrateNode,
  MigrateStatus,
  MigrateModelsEntityResult,
} from '~/models';
import { ContentTypesResult, MigrateRequest } from '~/models/Operations';
import ContensisRepository from '~/repositories/ContensisRepository';
import { AssetEntryBuilder } from '~/transformations/AssetEntryBuilder';
import { ContentTypeBuilder } from '~/transformations/ContentTypeBuilder';
import { EntryBuilder } from '~/transformations/EntryBuilder';
import mapErrorToObject from '~/transformations/mapAppErrorToObject';
import { mapMigrateResult } from '~/transformations/mapModelsMigrateResult';
import { TagGroupBuilder } from '~/transformations/TagGroupBuilder';
import { sortByStringInKey } from '~/util/array';
import { Logger, completeProgressBar, newProgressBar } from '~/util/logger';

class ModelMigrationService extends NodesMigrationService {
  inputIds = new Set<string>();
  migrate!: ResourcesToMigrate;
  migrateResult: MigrateModelsResult = {};
  progress: ProgressBar | undefined;

  /** Create a distinct list of inputIds from the provided array of inputs */
  private populateInputIds = (
    modelIdsOrModels: (string | ContentType | Component)[]
  ) => {
    for (const m of modelIdsOrModels)
      this.inputIds.add(typeof m === 'string' ? m : m.id);
  };

  /** Adds inputIds for all resources returned by hydrate if no input ids have been provided */
  private addAllInputIds = () => {
    if (this.inputIds.size === 0) {
      this.payload.models = ['*'];
      for (const c of [
        ...this.source.context.contentTypes,
        ...this.source.context.components,
      ])
        this.inputIds.add(c.id);
    }
  };

  // Expose these async getters to return assets from the sourceRepo for code
  // aesthetics and convenience when using as a library because
  // sourceRepo is normally implied in non-migratory usage
  contentTypes = async () => {
    if (!this.isHydrated) await this.HydrateContensisRepositories();
    return this.source.context.contentTypes;
  };
  components = async () => {
    if (!this.isHydrated) await this.HydrateContensisRepositories();
    return this.source.context.components;
  };
  tagGroups = async () => {
    if (!this.isHydrated) await this.HydrateContensisRepositories();
    return this.source.context.tagGroups;
  };
  contentModels = async (): Promise<Model[]> => {
    if (!this.isHydrated) await this.HydrateContensisRepositories();
    return deepCleaner(deepCleaner(this.source.context.models)) || [];
  };

  constructor(payload: MigrateRequest, preview: boolean) {
    super(payload, preview);
  }

  MigrateModels = async (
    modelIdsOrModels: (string | ContentType | Component)[] = []
  ): Promise<ContentTypesResult> => {
    const { inputIds, isPreview } = this;

    this.populateInputIds(modelIdsOrModels);

    await this.HydrateContensisRepositories(modelIdsOrModels);

    this.addAllInputIds();

    await this.GetResourcesToMigrate();

    await this.BuildAllMigrateResources();

    const previewResult = mapMigrateResult(this);

    if (!isPreview) {
      await this.CommitMigration();
      const result = mapMigrateResult(this, true);
      return result;
    }
    return previewResult;
  };

  Diff = async (
    modelIdsOrModels: (string | ContentType | Component)[],
    contentTypeIds: string[] = [],
    componentIds: string[] = []
  ) => {
    const { inputIds } = this;

    this.populateInputIds([
      ...contentTypeIds,
      ...componentIds,
      ...modelIdsOrModels,
    ]);
    await this.HydrateContensisRepositories(Array.from(inputIds));
    this.addAllInputIds();

    await this.GetResourcesToMigrate();
    // Building resources for each target repo from source data
    // also diffs them with any existing resource found in each target
    this.BuildResourcesToMigrate();
    const result = mapMigrateResult(this);
    Logger.info(`Done diffing`);
    return result;
  };

  /**
   * Create distinct lists of all resource dependencies to include in the migration
   */
  private GetResourcesToMigrate = async (): Promise<ResourcesToMigrate> => {
    const { includeDefaults } = this.payload;
    const { context } = this.source;

    const contentTypeIds = new Set<string>();
    const componentIds = new Set<string>();
    const tagGroupIds = new Set<string>();
    const entryIds = new Set<string>();
    const nodes = new Map<string, Node>();

    // Keep a note of what we have already checked so we don't check it again when it crops up
    const idsChecked = new Set<string>();

    const extractDependencies = (id: string, thisList: Set<string>) => {
      if (idsChecked.has(id)) return;

      thisList.add(id);

      const model = context.models.find((m) => m.id === id);
      if (model) {
        for (const d of model.dependencies?.contentTypes || [])
          contentTypeIds.add(d[0]);
        for (const d of model.dependencies?.components || [])
          componentIds.add(d[0]);
        for (const d of model.dependencies?.tagGroups || [])
          tagGroupIds.add(d[0]);
        if (includeDefaults) {
          for (const d of model.defaults || []) entryIds.add(d[0]);
          for (const d of model?.nodes || []) {
            const node = context.nodes.find((n) => n.id === d[0]);
            if (node) nodes.set(node.id, node);
          }
        }
      } else {
        const { dependencies } = context.dependencies.getById(id) || {};

        for (const depId of dependencies?.contentTypes || [])
          contentTypeIds.add(depId);
        for (const depId of dependencies?.components || [])
          componentIds.add(depId);
        for (const depId of dependencies?.tagGroups || [])
          tagGroupIds.add(depId);
        if (includeDefaults) {
          for (const depId of dependencies?.defaults || [])
            entryIds.add(depId[0]);
          for (const depId of dependencies?.nodes || []) {
            const node = context.nodes.find((n) => n.id === depId[0]);
            if (node) nodes.set(node.id, node);
          }
        }
      }
      idsChecked.add(id);
    };

    // Loop through source models and extract all dependencies into distinct sets
    const { assert, resolve } = context;
    for (const id of this.inputIds) {
      // We might not know if input id is a content type or component at this point
      // so we will resolve the resource by id and assert what it is
      const resource = resolve.resource(id);
      if (resource) {
        const isContentType = assert.isContentType(resource as ContentType);
        extractDependencies(id, isContentType ? contentTypeIds : componentIds);
      } else {
        this.AddError(id, new Error(`Cannot find input id ${id}`));
      }
    }

    // Loop over all dependencies to see if we need to include dependencies of their own
    for (const id of contentTypeIds) extractDependencies(id, contentTypeIds);
    for (const id of componentIds) extractDependencies(id, componentIds);

    this.migrate = {
      contentTypeIds: Array.from(contentTypeIds),
      componentIds: Array.from(componentIds),
      entryIds: Array.from(entryIds),
      nodes: Array.from(nodes.values()),
      tagGroupIds: Array.from(tagGroupIds),
    };
    this.source.models.resources = this.migrate;

    for (const [projectId, target] of Object.entries(this.targets)) {
      await Promise.all([
        target.entries.GetDependentEntries(
          context.entries.map((e) => target.transformGuid(e.sys.id || '')),
          this.migrate.contentTypeIds.length,
          'content types',
          0
        ),
        target.entries.GetDependentNodes(
          new Map<string, string>(
            this.migrate.nodes.map((n) => [n.id, n.path])
          ),
          1,
          'models to migrate'
        ),
      ]);
    }

    return this.migrate;
  };

  /**
   * Build resources for each target repository in a specific order
   */
  private BuildAllMigrateResources = async () => {
    const { migrate, source } = this;

    // ** Tag groups **
    // Tag groups have no dependencies of their own but are a dependency
    // of content types or components
    if (migrate.tagGroupIds.length) {
      source.tags.groups = source.context.tagGroups.filter((tg) =>
        migrate.tagGroupIds.includes(tg.id)
      );
      this.BuildTagGroupsToMigrate();
    }

    // ** Nodes **
    // Building dependent nodes first so we can get a handle
    // on the new defaultParentNodeId when we build the content types
    if (migrate.nodes.length) {
      source.nodes.raw = migrate.nodes.sort(sortByStringInKey('path'));
      //await this.GetNodes(); // TODO: avoid calling this
      await this.BuildNodesToMigrate();
    }

    // ** Content types / components **
    this.BuildResourcesToMigrate();

    // ** Nodes **
    if (migrate.nodes.length) {
      // reset migrateNodes so we can rebuild them after building the content types
      Object.values(this.targets).forEach((t) => (t.nodes.migrateNodes = []));
      await this.BuildNodesToMigrate();
      // Check that entries mapped into built nodes exist
      // if any entryId attached nodes are not resolved we can safely ignore
      // and remove them as we are migrating models with default nodes
      await this.GetNodeEntries({ ignoreErrors: true });
    }

    // ** Entries **
    if (migrate.entryIds.length) {
      source.entries.raw = source.context.entries.filter((e) =>
        migrate.entryIds.includes(e.sys.id || '')
      );
      await this.BuildTargetEntries();
    }
  };

  /**
   * Build content types and component resources for each target repository
   */
  private BuildResourcesToMigrate = () => {
    const { inputIds, migrate: resources, targets } = this;
    const ctIds = resources.contentTypeIds.length;
    const cmpIds = resources.componentIds.length;
    const mdlIds = inputIds.size;
    Logger.info(
      `Building ${pl('content type', ctIds, true)}${
        cmpIds ? ` and ${pl('component', cmpIds, true)}` : ''
      } for ${pl('model', mdlIds, true)} in ${Logger.standardText(
        Object.keys(targets).join(', ')
      )}`,
      true
    );
    for (const target of Object.values(targets))
      target.models.BuildResourcesToMigrate(resources);
  };

  private CommitMigration = async () => {
    Logger.info(`Committing model ${this.opName}...`, true);
    const { migrateResult, payload, queue } = this;

    // Callback function for filtering migrate arrays
    const status =
      (migrateStatus: MigrateStatus) =>
      ({ status }: { status: MigrateStatus }) =>
        status === migrateStatus;
    const result =
      (migrateResult: keyof MigrateModelsEntityResult) =>
      ({ result }: { result?: keyof MigrateModelsEntityResult }) =>
        result === migrateResult;

    // Load any default assets first as we don't need to depend on any created content types to process these
    await this.DownloadAndQueueContentAssets(false);

    // We see 500 errors if we take this above 2,
    // override anything set by the user to 1
    this.queue.concurrency = 1;

    for (const [projectId, target] of Object.entries(this.targets)) {
      const contentTypes = target.models.migrate.contentTypes;
      const components = target.models.migrate.components;
      const tagGroups = target.tags.migrateGroups;
      const nodes = target.nodes.migrateNodes;
      const assets: AssetEntryBuilder[] = [];
      const entries: EntryBuilder[] = [];
      for (const me of target.entries.migrate) {
        if (me.isAsset()) assets.push(me);
        else entries.push(me);
      }

      // Populate migrate result object (default assets already loaded)
      migrateResult[projectId] = {
        contentTypes: {
          unchanged: contentTypes.filter(status('no change')).map((c) => c.id),
          created: [],
          updated: [],
          errors: [],
        },
        components: {
          unchanged: components.filter(status('no change')).map((c) => c.id),
          created: [],
          updated: [],
          errors: [],
        },
        defaults: {
          unchanged: target.entries.migrate
            .filter(status('no change'))
            .map((c) => c.id),
          created: assets.filter(result('created')).map((c) => c.id),
          updated: assets.filter(result('updated')).map((c) => c.id),
          published: [],
          errors: assets
            .filter(result('errors'))
            .map((c) => [c.id, c.error as MappedError]),
        } as MigrateModelsEntityResult & { published: string[] },
        nodes: {
          unchanged: nodes
            .filter(status('no change'))
            .filter((c) => c.parentId)
            .map((c) => c.id),
          created: [],
          updated: [],
          errors: [],
        },
        tagGroups: {
          unchanged: tagGroups.filter(status('no change')).map((g) => g.id),
          created: [],
          updated: [],
          errors: [],
        },
      };
      const everything = [
        ...contentTypes,
        ...components,
        ...nodes,
        ...assets,
        ...entries,
        ...tagGroups,
      ];
      const twoPassTotal = everything.filter(
        (r) => r.status === 'two-pass'
      ).length;

      const total = everything.filter((r) =>
        ['create', 'update', 'two-pass'].includes(r.status)
      ).length;

      this.progress = newProgressBar(
        ` Loading content models :bar :percent ${Logger.infoText(
          ':current/:total :elapseds/:etas :ratep/s'
        )}`,
        {
          output: payload.outputProgress,
          complete: Logger.successText('='),
          head: Logger.successText('>'),
          incomplete: ' ',
          width: 30,
          total,
        }
      );

      // Create tag groups before anything else as they contain no dependencies
      for (const create of tagGroups.filter(status('create')))
        void queue.add(() => this.CommitModelTagGroup(create, target));

      // Create any default nodes from any content types first
      for (const create of nodes.filter(status('create')))
        void queue.add(() => this.CommitModelNode(create, target));

      // Create components first as they will be from content types
      for (const create of components.filter(status('create')))
        void queue.add(() => this.CommitModelResource(create));

      for (const create of components.filter(status('two-pass')))
        void queue.add(() => this.CommitModelResource(create, true));

      // Let the components create before moving onto content types
      await queue.onIdle();

      // Create all content types next, all assets and stubs should exist now
      for (const create of contentTypes.filter(status('create')))
        void queue.add(() => this.CommitModelResource(create));

      for (const create of contentTypes.filter(status('two-pass')))
        void queue.add(() => this.CommitModelResource(create, true));

      // Let the first pass content types finish loading
      await queue.onIdle();

      // Load first pass of any default entry here
      // Potentially with a special mapping for a stub content type that doesn't have all fields
      for (const create of entries.filter(status('create')))
        void queue.add(() => this.CommitModelEntry(create, true));

      for (const create of entries.filter(status('two-pass')))
        void queue.add(() => this.CommitModelEntry(create, true));

      // Let the first pass default entries and nodes finish loading before moving onto updating components
      await queue.onIdle();

      // Do all updates next, all dependencies should have been created in some form by now
      for (const update of components.filter(status('update')))
        void queue.add(() => this.CommitModelResource(update));

      for (const update of contentTypes.filter(status('update')))
        void queue.add(() => this.CommitModelResource(update));

      // Finish updating content types and components before updating any default entries or nodes
      await queue.onIdle();

      for (const update of tagGroups.filter(status('update')))
        void queue.add(() => this.CommitModelTagGroup(update, target));

      for (const update of entries.filter(status('update')))
        void queue.add(() => this.CommitModelEntry(update));

      // Don't make updates to default nodes if they already exist
      // for (const update of status.update?.nodes || [])
      //   void queue.add(() => this.CommitModelNode(update, target));

      await queue.onIdle();

      completeProgressBar();
      this.progress = newProgressBar(
        ` Second pass models :bar :percent ${Logger.infoText(
          ':current/:total :elapseds/:etas :ratep/s'
        )}`,
        {
          output: this.payload.outputProgress,
          complete: Logger.successText('='),
          head: Logger.successText('>'),
          incomplete: ' ',
          width: 30,
          total: twoPassTotal,
        }
      );

      // Finally run the second pass of updates over the two-pass assets we created before
      for (const secondPass of components.filter(status('two-pass')))
        void queue.add(() => this.CommitModelResource(secondPass));

      for (const secondPass of contentTypes.filter(status('two-pass')))
        void queue.add(() => this.CommitModelResource(secondPass));

      //  Allow component, contentType and second-pass updates to load concurrently
      await queue.onIdle();

      // Run a second pass of an entry so we can remap it and check for any updates
      for (const secondPass of entries.filter(status('create')))
        void queue.add(() => this.CommitModelEntry(secondPass));

      for (const secondPass of entries.filter(status('two-pass')))
        void queue.add(() => this.CommitModelEntry(secondPass));

      for (const publish of entries.filter(status('publish')))
        void queue.add(() => this.PublishModelEntry(publish));

      // Finally wait for the second-pass updates, and any publish actions to finish for any default entries
      await queue.onIdle();
      completeProgressBar();
    }

    Logger.success(
      `Model ${this.opName} from ${this.source.project.id} to ${Object.keys(
        this.targets
      ).join(', ')} complete.`
    );

    return this.migrateResult;
  };

  private CommitModelTagGroup = async (
    builtTagGroup: TagGroupBuilder,
    target: ContensisRepository
  ) => {
    const [err] = await this.CommitTagGroup(builtTagGroup, target);
    if (err) {
      if (builtTagGroup.error)
        this.migrateResult[target.projectId].tagGroups.errors.push([
          builtTagGroup.id,
          builtTagGroup.error,
        ]);
    } else {
      this.migrateResult[target.projectId].tagGroups[
        builtTagGroup.status === 'update' ? 'updated' : 'created'
      ].push(builtTagGroup.id);
    }
    this.progress?.tick();
  };

  private CommitModelNode = async (
    node: MigrateNode,
    target: ContensisRepository
  ) => {
    const result = await this.CommitNode(target, node);
    if (result)
      if (result === 'errors') {
        if (node.error)
          this.migrateResult[target.projectId].nodes.errors.push([
            node.id,
            node.error,
          ]);
      } else this.migrateResult[target.projectId].nodes[result].push(node.id);
    this.progress?.tick();
  };

  private CommitModelEntry = async (me: EntryBuilder, firstPass = false) => {
    let fromModel = true;
    let finalPass = !firstPass;
    if (!me.isAsset()) {
      // Rebuild the entry for first and second pass
      me.init();
      me.build();
      // We don't really want to deal with two-pass entries here
      // TODO: need a strategy for importing a two-pass without links to other resources
      if (me.status === 'two-pass') {
        fromModel = false;
        finalPass = false; // Commit a firstPass version of the entry
      }
      const [error, created] = await this.CommitEntry(
        me,
        !finalPass,
        fromModel
      );
      if (me.result === 'errors') {
        if (error)
          this.migrateResult[me.contensis.projectId].defaults.errors.push([
            me.id,
            error,
          ]);
      } else if (
        created &&
        me.result &&
        // Don't record a second "created" result for a second pass
        (me.status !== 'two-pass' || (me.status === 'two-pass' && !firstPass))
      )
        this.migrateResult[me.contensis.projectId].defaults[me.result].push(
          me.id
        );
    }
    this.progress?.tick();
  };

  private PublishModelEntry = async (me: EntryBuilder) => {
    if (me.existing) {
      const [err, published] = await to(
        me.contensis.repo.PublishEntry(me.existing)
      );
      if (err) {
        const mappedErr = this.AddError(me.id, err);
        me.error = mappedErr;
        this.migrateResult[me.contensis.projectId].defaults.errors.push([
          me.id,
          err,
        ]);
      }
      if (published) {
        this.published.push(me.id);

        (
          this.migrateResult[me.contensis.projectId]
            .defaults as MigrateModelsEntityResult & { published: string[] }
        ).published.push(me.id);
      }
    }
    this.progress?.tick();
  };

  private CommitModelResource = async (
    resource: ContentTypeBuilder<ContentType> | ContentTypeBuilder<Component>,
    firstPass = false
  ) => {
    const { contensis, context, status } = resource;
    const type = resource.isContentType() ? 'contentTypes' : 'components';

    Logger.info(
      `[${status}${firstPass ? ' stub' : ''}] ${type} '${resource.id}' in ${
        context.project?.id
      }`
    );

    // only migrate if we have not already recorded an error for this id
    if (this.errors[resource.id]) {
      this.progress?.tick();
      return;
    }

    let error: Error | null;
    let created: ContentType | Component | undefined;
    const contentTypeOrComponent = resource.toJSON({ firstPass });
    if (context.assert.isContentType(contentTypeOrComponent)) {
      [error, created] = await contensis.repo.UpsertContentType(
        resource?.existing as ContentType,
        contentTypeOrComponent
      );
      // Update context so we can accurately rebuild entries with existing resources
      if (created)
        if (!contensis.context.resolve.contentType(resource.id))
          contensis.context.contentTypes.push(created);
        else
          contensis.context.contentTypes[
            contensis.context.contentTypes.findIndex(
              (c) => c.id === resource.id
            )
          ] = created;
    } else {
      [error, created] = await contensis.repo.UpsertComponent(
        resource?.existing as Component,
        contentTypeOrComponent
      );
      // Update context so we can accurately rebuild entries with existing resources
      if (created)
        if (!contensis.context.resolve.component(resource.id))
          contensis.context.components.push(created);
        else
          contensis.context.components[
            contensis.context.components.findIndex((c) => c.id === resource.id)
          ] = created;
    }
    // Update existing resource with created for two-pass so on the
    // second pass we can discover the existing item we've previously created
    if (firstPass && created) resource.existing = created;

    // Get a reference to the migration results for this project and resource type
    const results = this.migrateResult[contensis.projectId][type];

    if (error) {
      results.errors.push([resource.id, mapErrorToObject(error)]);
      this.AddError(resource.id, error);
    } else if (
      created &&
      // Don't record a second "created" result for a second pass
      (status !== 'two-pass' || (status === 'two-pass' && !firstPass))
    )
      // Map the status to the operation we've performed and push the id to indicate the result
      results[status === 'update' ? 'updated' : 'created'].push(created.id);

    this.progress?.tick();
  };
}

export default ModelMigrationService;
